/**
 * The plan card (mission 9 WP13 T079; sheet 50 callouts 2 and 3; B.1, B.2, D.8; FR-009, FR-010;
 * contracts/ui-api.md "Sheet 50", "The refused surfaces"; research §1.16, §1.17; spec amendment 12).
 *
 * - The plan and its price (`planMonthly` / `planAnnual`, the price through `money()`, C-010), then
 *   `planRenews`, or `planEnds` while cancelling. A read-only account states neither: its band does.
 * - Cancel (`planCancel`) opens a focus-trapped dialog, `planCancelConfirmBody` with the annual clause on
 *   the annual plan, `planCancelKeep` first; Esc and Keep plan restore focus to Cancel. While cancelling,
 *   `planUndo` keeps the plan. Cancel and undo stay enabled while read-only (B.1 "cancel any time").
 * - The switch (`planSwitchTo`, the other plan's price from `prices`) is scheduled for the next renewal
 *   (`planSwitchNote`); once scheduled, `planSwitchPending`. It is refused while read-only through
 *   `useBillingGate` (amendment 8), and hidden while cancelling (the server answers `cancelling`).
 * - A refusal renders its register row (the 409 `access-ended` with Subscribe), never the detail (U25).
 *
 * States: `monthly`, `annual`, `cancelling`, `switch-pending`, `switch-refused-read-only`,
 * `cancel-dialog-open`, `cancel-dialog-annual`.
 */
import { useId, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import type { BillingPlanView, BillingView, PlanId, PlanPrices } from '@/lib/api/billing'
import { gateButton, useBillingGate } from '@/lib/billing-gate'
import { date, money } from '@/lib/format'
import { PROVISIONAL_COPY, copyText, type TextKey } from '@/lib/provisional-copy'
import { useCancelMutation, usePlanSwitchMutation, useUndoCancelMutation } from '@/lib/queries'

import { BillingProblem } from './BillingProblem'

const PLAN: Record<PlanId, { label: TextKey; other: PlanId; interval: 'month' | 'year'; price: keyof PlanPrices }> = {
  monthly: { label: 'planLabelMonthly', other: 'annual', interval: 'month', price: 'monthly_cents' },
  annual: { label: 'planLabelAnnual', other: 'monthly', interval: 'year', price: 'annual_cents' },
}

/** "annual plan": the register's label, inside a sentence. */
const planName = (id: PlanId) => copyText(PLAN[id].label).toLowerCase()

function planLine(plan: BillingPlanView): string {
  const args = { price: money(plan.price_cents), interval: plan.interval }
  return plan.id === 'annual' ? PROVISIONAL_COPY.planAnnual.render(args) : PROVISIONAL_COPY.planMonthly.render(args)
}

export function PlanCard({ view }: { view: BillingView }) {
  const gate = useBillingGate()
  const cancel = useCancelMutation()
  const undo = useUndoCancelMutation()
  const switchPlan = usePlanSwitchMutation()
  const [dialogOpen, setDialogOpen] = useState(false)
  const bodyId = useId()
  const { plan, access } = view

  function onOpenChange(next: boolean) {
    cancel.reset()
    setDialogOpen(next)
  }

  const cardError = undo.error ?? switchPlan.error
  const endsOn = view.period_end ?? view.renews_at
  let dates: string | null = null
  if (plan !== null && access.state !== 'read_only') {
    if (access.cancelling && view.period_end !== null) dates = PROVISIONAL_COPY.planEnds.render({ date: date(view.period_end) })
    else if (!access.cancelling && view.renews_at !== null) dates = PROVISIONAL_COPY.planRenews.render({ date: date(view.renews_at) })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{copyText('planCardTitle')}</CardTitle>
      </CardHeader>
      {plan === null ? null : (
        <CardContent className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <p className="text-body font-medium text-navy-950">{planLine(plan)}</p>
            {dates === null ? null : <p className="text-body-sm text-navy-700">{dates}</p>}
          </div>
          {plan.pending_switch !== null && plan.switch_at !== null ? (
            <div className="flex flex-col gap-1">
              <p className="text-body-sm text-navy-950">
                {PROVISIONAL_COPY.planSwitchPending.render({ plan: planName(plan.pending_switch), date: date(plan.switch_at) })}
              </p>
              <p className="text-body-sm text-navy-600">{copyText('planSwitchNote')}</p>
            </div>
          ) : null}
          {cardError === null ? null : <BillingProblem error={cardError} />}
          <div className="flex flex-wrap items-center gap-3">
            {access.cancelling ? (
              <Button
                variant="secondary"
                loading={undo.isPending}
                onClick={() => {
                  switchPlan.reset()
                  undo.mutate()
                }}
              >
                {copyText('planUndo')}
              </Button>
            ) : (
              <>
                {plan.pending_switch === null ? (
                  <Button
                    variant="secondary"
                    loading={switchPlan.isPending}
                    onClick={() => {
                      undo.reset()
                      switchPlan.mutate(PLAN[plan.id].other)
                    }}
                    {...gateButton(gate)}
                  >
                    {PROVISIONAL_COPY.planSwitchTo.render({
                      plan: planName(PLAN[plan.id].other),
                      price: money(view.prices[PLAN[PLAN[plan.id].other].price]),
                      interval: PLAN[PLAN[plan.id].other].interval,
                    })}
                  </Button>
                ) : null}
                <Dialog open={dialogOpen} onOpenChange={onOpenChange}>
                  <DialogTrigger asChild>
                    <Button variant="danger-secondary">{copyText('planCancel')}</Button>
                  </DialogTrigger>
                  <DialogContent size="sm" aria-describedby={bodyId}>
                    <DialogHeader>
                      <DialogTitle>{copyText('planCancelConfirmTitle')}</DialogTitle>
                    </DialogHeader>
                    <DialogBody className="flex flex-col gap-4">
                      <DialogDescription id={bodyId}>
                        {/* With neither date, no dated sentence, as BillingBanner does (mission 9 review UI3). */}
                        {endsOn === null
                          ? null
                          : PROVISIONAL_COPY.planCancelConfirmBody.render({ date: date(endsOn), annual: plan.id === 'annual' })}
                      </DialogDescription>
                      {cancel.error === null ? null : <BillingProblem error={cancel.error} />}
                    </DialogBody>
                    <DialogFooter>
                      <DialogClose asChild>
                        <Button variant="ghost">{copyText('planCancelKeep')}</Button>
                      </DialogClose>
                      <Button
                        variant="danger-secondary"
                        loading={cancel.isPending}
                        onClick={() => cancel.mutate(undefined, { onSuccess: () => setDialogOpen(false) })}
                      >
                        {copyText('planCancel')}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
          {plan.pending_switch === null && !access.cancelling ? (
            <p className="text-caption text-navy-600">{copyText('planSwitchNote')}</p>
          ) : null}
        </CardContent>
      )}
    </Card>
  )
}
